import { inject, Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { GrowthBook, InitResponse } from '@growthbook/growthbook';
import { getParams, UaParserService, WINDOW } from '@hglobal/common';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';
import { environment } from '@fb/environments';
import {
  EnvName,
  GrowthBookAttributes,
  GrowthBookFeatures,
  GrowthBookProfile,
} from '@fb/models';

const GB_TEST_LS_KEY = 'gb-test';
const GB_DEBUG_LS_KEY = 'puGBDebug';
const GROWTH_BOOK_ATTRIBUTE_KEYS = [
  'id',
  'email',
  'gender',
  'age',
  'birthday',
  'startDate',
  'location',
  'businessUnit',
  'service',
  'team',
  'function',
  'position',
  'status',
  'seniority',
  'workFormat',
  'deviceType',
  'osName',
  'gbTest',
] satisfies Array<keyof GrowthBookAttributes>;

interface GbWindow extends Window {
  dataLayer?: Array<Record<string, unknown>>;
  puAssignExperiment?: (experiment: string, isForceTrack?: boolean) => Promise<void>;
  puForceExperimentVariation?: (key: string, variation: number) => void;
  puGBAttributes?: () => GrowthBookAttributes;
  puFbAssignExperiment?: (experiment: string, isForceTrack?: boolean) => Promise<void>;
  puFbForceExperimentVariation?: (key: string, variation: number) => void;
  puFbGBAttributes?: () => GrowthBookAttributes;
}

type ExperimentResult = {
  inExperiment: boolean;
  variationId?: number;
};

@Injectable({ providedIn: 'root' })
export class GrowthBookService {
  readonly #window = inject<GbWindow>(WINDOW);
  readonly #http = inject(HttpClient);
  readonly #uaParser = inject(UaParserService);
  readonly #ngZone = inject(NgZone);

  readonly #hasGbKey = !!(environment.growthBook?.clientKey && environment.growthBook?.apiHost);
  readonly #isGbTest = this.#window.localStorage.getItem(GB_TEST_LS_KEY) === 'true';
  readonly #isGbDebugMode
    = environment.envName === EnvName.DEV
    || this.#window.localStorage.getItem(GB_DEBUG_LS_KEY) === 'true';

  readonly #featuresLoadedSubject = new BehaviorSubject<boolean>(false);
  readonly #featureUpdatesSubject = new BehaviorSubject<void>(undefined);

  #profileId: number | null = null;
  #syncAttributesInFlight: Promise<void> | null = null;

  readonly #gbInstance = new GrowthBook<GrowthBookFeatures>({
    apiHost: this.#hasGbKey ? environment.growthBook.apiHost : null,
    clientKey: this.#hasGbKey ? environment.growthBook.clientKey : null,
    enableDevMode: this.#isGbDebugMode,
    disableCache: this.#isGbDebugMode,
    attributes: {},
    trackingCallback: (experiment, result) => {
      this.#emitExperimentViewedEvent(experiment.key, result.variationId);
    },
  });

  async init(): Promise<InitResponse> {
    if (!this.#hasGbKey) {
      console.warn('[GrowthBook] No clientKey/apiHost — running in allow-all fallback mode');
      this.#featuresLoadedSubject.next(true);

      return {} as InitResponse;
    }

    this.#gbInstance.setRenderer(() => this.#emitFeatureUpdate());

    const result = await this.#gbInstance.init({ streaming: true });

    if (result?.success) {
      this.#registerGlobalFunctions();
    } else if (result?.error) {
      console.warn('GrowthBook init error: ', result.error);
    }

    this.#featuresLoadedSubject.next(true);

    return result;
  }

  getFeatureValue<T extends string | number | boolean | object>(
    key: keyof GrowthBookFeatures,
    defaultValue: T,
  ): Observable<T> {
    return this.#whenTargeted().pipe(
      switchMap(() => this.#featureUpdatesSubject.pipe(
        map(() => this.#getFeatureValueSnapshot(key, defaultValue)),
        distinctUntilChanged(),
      )),
    );
  }

  async syncAttributes(): Promise<void> {
    if (this.#profileId) {
      return;
    }

    if (this.#syncAttributesInFlight) {
      return this.#syncAttributesInFlight;
    }

    this.#syncAttributesInFlight = this.#loadProfileAttributes();

    try {
      await this.#syncAttributesInFlight;
    } finally {
      this.#syncAttributesInFlight = null;
    }
  }

  async updateAttributes(attributes: Partial<GrowthBookAttributes>): Promise<void> {
    await this.#gbInstance.setAttributes({
      ...this.#gbInstance.getAttributes(),
      ...attributes,
    });
  }

  async assignExperiment(experiment: string, isForceTrack = false): Promise<void> {
    const experiments = this.#getAssignedExperiments();
    const isAlreadyAssigned = experiments.includes(experiment);

    if (!isAlreadyAssigned) {
      await this.updateAttributes({ experiments: [...experiments, experiment] });
    }

    if (isForceTrack) {
      await this.#forceTrackExperiment(experiment, isAlreadyAssigned);
    }
  }

  forceVariation(key: string, variation: number): void {
    this.#gbInstance.forceVariation(key, variation);
  }

  getAttributes(): GrowthBookAttributes {
    return this.#gbInstance.getAttributes() as GrowthBookAttributes;
  }

  async #loadProfileAttributes(): Promise<void> {
    try {
      const profile = await firstValueFrom(
        this.#http.get<GrowthBookProfile>(`${environment.apiHost}user/personal-info/`),
      );

      if (!profile?.id) {
        return;
      }

      this.#profileId = profile.id;
      await this.updateAttributes(this.#extractAttributes(profile));
      this.#emitFeatureUpdate();
    } catch {
      console.warn('[GrowthBook] Failed to load personal-info for attributes');
    }
  }

  #extractAttributes(profile: GrowthBookProfile): GrowthBookAttributes {
    return getParams(
      {
        ...profile,
        deviceType: this.#uaParser.deviceType,
        osName: this.#uaParser.osName,
        gbTest: this.#isGbTest ? 'true' : 'false',
      },
      GROWTH_BOOK_ATTRIBUTE_KEYS,
    ) as GrowthBookAttributes;
  }

  #registerGlobalFunctions(): void {
    const assignExperiment = async (experiment: string, isForceTrack = false) => {
      await this.assignExperiment(experiment, isForceTrack);
    };
    const forceVariation = (key: string, variation: number) => {
      this.forceVariation(key, variation);
    };
    const getAttributes = () => this.getAttributes();

    this.#window.puFbAssignExperiment = assignExperiment;
    this.#window.puFbForceExperimentVariation = forceVariation;
    this.#window.puFbGBAttributes = getAttributes;
    this.#window.puAssignExperiment ??= assignExperiment;
    this.#window.puForceExperimentVariation ??= forceVariation;
    this.#window.puGBAttributes ??= getAttributes;
  }

  async #forceTrackExperiment(experiment: string, isAlreadyAssigned: boolean): Promise<void> {
    if (!this.#hasGbKey) {
      return;
    }

    await this.#gbInstance.refreshFeatures({ skipCache: false });

    if (!isAlreadyAssigned) {
      return;
    }

    const experimentResult = this.#getExperimentResult(experiment);

    if (experimentResult?.inExperiment && experimentResult.variationId !== undefined) {
      this.#emitExperimentViewedEvent(experiment, experimentResult.variationId);
    }
  }

  #getAssignedExperiments(): string[] {
    const rawExperiments = (this.#gbInstance.getAttributes() as GrowthBookAttributes)?.experiments;

    return Array.isArray(rawExperiments) ? [...rawExperiments] : [];
  }

  #getExperimentResult(experimentId: string): ExperimentResult | null {
    const results = Array.from(this.#gbInstance.getAllResults().values());
    const experimentResult = results.find(result => result.experiment?.key === experimentId);

    return experimentResult
      ? {
        inExperiment: !!experimentResult.result?.inExperiment,
        variationId: experimentResult.result?.variationId,
      }
      : null;
  }

  #emitExperimentViewedEvent(experimentId: string, variationId: number): void {
    this.#window.dataLayer?.push({
      event: 'experiment_viewed',
      experiment_id: experimentId,
      variation_id: variationId,
    });
  }

  #emitFeatureUpdate(): void {
    this.#ngZone.run(() => this.#featureUpdatesSubject.next());
  }

  #getFeatureValueSnapshot<T extends string | number | boolean | object>(
    key: keyof GrowthBookFeatures,
    defaultValue: T,
  ): T {
    return this.#hasGbKey ? this.#gbInstance.getFeatureValue(key, defaultValue) as T : defaultValue;
  }

  #whenTargeted(): Observable<void> {
    if (!this.#hasGbKey) {
      return of(undefined as void);
    }

    return this.#featuresLoadedSubject.pipe(
      filter(Boolean),
      take(1),
      switchMap(() => (this.#isTargeted()
        ? of(undefined as void)
        : this.#featureUpdatesSubject.pipe(
          filter(() => this.#isTargeted()),
          take(1),
          map((): void => undefined),
        ))),
    );
  }

  #isTargeted(): boolean {
    return !!this.#profileId && this.getAttributes()?.id === this.#profileId;
  }
}
